// Player help message dismissal state.
//
// Persistent "Don't show again" state lives on the server per-user
// (/api/me/help-messages/:id). In-session suppression — "I dismissed it
// without ticking the box; don't show it again this game" — is kept in
// sessionStorage so it survives in-app navigation but not a tab close.
//
// A help message is identified by a stable string (e.g. "personal-
// visibility"). Callers:
//   - isHelpMessageSuppressedInGame(gameId, id) for the session check (sync)
//   - await isHelpMessageDismissed(id) for the persistent check
//   - suppressHelpMessageInGame(gameId, id) after a non-persistent dismiss
//   - setHelpMessageDismissed(id, true) when the user ticks "don't show again"

import { api } from './api.js';

const SESSION_KEY_PREFIX = 'help-suppressed:';

function sessionKey(gameID, helpID) {
  return `${SESSION_KEY_PREFIX}${gameID}:${helpID}`;
}

/**
 * @param {string} gameID
 * @param {string} helpID
 * @returns {boolean}
 */
export function isHelpMessageSuppressedInGame(gameID, helpID) {
  if (!gameID) return false;
  try {
    return sessionStorage.getItem(sessionKey(gameID, helpID)) === '1';
  } catch {
    return false;
  }
}

/**
 * @param {string} gameID
 * @param {string} helpID
 */
export function suppressHelpMessageInGame(gameID, helpID) {
  if (!gameID) return;
  try {
    sessionStorage.setItem(sessionKey(gameID, helpID), '1');
  } catch {
    // sessionStorage unavailable — nothing to do.
  }
}

/**
 * @param {string} helpID
 * @returns {Promise<boolean>}
 */
export async function isHelpMessageDismissed(helpID) {
  try {
    const state = await api.get(`/api/me/help-messages/${encodeURIComponent(helpID)}`);
    return Boolean(state?.dismissed);
  } catch (err) {
    console.warn('help-state: failed to load dismissal state', err);
    return false;
  }
}

/**
 * @param {string} helpID
 * @param {boolean} dismissed
 * @returns {Promise<void>}
 */
export async function setHelpMessageDismissed(helpID, dismissed) {
  try {
    await api.put(`/api/me/help-messages/${encodeURIComponent(helpID)}`, { dismissed });
  } catch (err) {
    console.warn('help-state: failed to save dismissal state', err);
  }
}
